document.addEventListener('alpine:init', () => {
  Alpine.data('ProductForm', () => ({
    formEl: null,
    addonBusy: false,
    init() {
      this.formEl = this.$root.querySelector('form');

      if (this.formEl.closest('[data-product-root]')?.querySelector('[data-mot-display-labels-addon]')) {
        // Submit both items before UpCart's single-product button/form handlers.
        this.addonClickHandler = (event) => {
          const button = event.target.closest('.add-to-cart');
          if (!button || button.form !== this.formEl) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submitAddon();
        };
        this.addonSubmitHandler = (event) => {
          if (event.target !== this.formEl) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submitAddon();
        };
        window.addEventListener('click', this.addonClickHandler, true);
        window.addEventListener('submit', this.addonSubmitHandler, true);
      } else {
        this.formEl.addEventListener('submit', this.submit.bind(this));
      }
    },
    destroy() {
      if (this.addonClickHandler) window.removeEventListener('click', this.addonClickHandler, true);
      if (this.addonSubmitHandler) window.removeEventListener('submit', this.addonSubmitHandler, true);
    },
    async submitAddon() {
      if (this.addonBusy || !this.formEl.reportValidity()) return;
      this.addonBusy = true;
      try {
        await this.submit({ target: this.formEl, preventDefault() {} });
      } finally {
        this.addonBusy = false;
      }
    },
    async submit(event) {
      event.preventDefault();

      const formEl = event.target;
      const personalisationRoot = formEl.closest('[data-product-content]') || document;
      const monogramInput = formEl.querySelector('input[name="properties[Custom Monogramming]"]');
      const selectedMonogramOption = formEl.querySelector('[name="options[Add monogramming?]"]:checked');
      const declinedPersonalisationOption = Array.from(
        personalisationRoot.querySelectorAll('input[name^="options["]:checked')
      ).find((option) => {
        const optionName = option.name.toLowerCase();
        const isPersonalisation = optionName.includes('monogram') || optionName.includes('laser engraving');
        return isPersonalisation && /^no\b/i.test(option.value.trim());
      });
      const monogramSelected =
        !monogramInput?.disabled &&
        (selectedMonogramOption
          ? selectedMonogramOption.value.trim().toLowerCase() !== 'no'
          : monogramInput?.required);
      const monogramUpsellDismissedToday = (() => {
        if (declinedPersonalisationOption?.name.toLowerCase().includes('monogram')) return false;
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
          now.getDate()
        ).padStart(2, '0')}`;

        try {
          return localStorage.getItem('motMonogramUpsellDismissedDate') === today;
        } catch (error) {
          return false;
        }
      })();

      const addToCartButton = formEl.querySelector('.add-to-cart');
      const inventoryManagement = addToCartButton?.dataset.inventoryManagement;
      const inventoryPolicy = addToCartButton?.dataset.inventoryPolicy;
      const forcePreorder = addToCartButton?.dataset.forcePreorder === 'true';
      const requested = Math.max(
        1,
        Number.parseInt(formEl.elements.namedItem('quantity')?.value || '1', 10) || 1
      );
      const available = Math.max(
        0,
        Number.parseInt(addToCartButton?.dataset.inventoryQuantity || '0', 10) || 0
      );
      const isPreorder =
        forcePreorder ||
        (inventoryManagement === 'shopify' && inventoryPolicy === 'continue' && available <= 0);
      const rollsIntoPreorder =
        inventoryManagement === 'shopify' &&
        inventoryPolicy === 'continue' &&
        available > 0 &&
        requested > available;
      const preorderConfirmed = formEl.dataset.motPreorderConfirmed === 'true';
      const skipPreorderNotice = Boolean(formEl.closest('[data-mot-diary-template="true"]'));
      const forceDiaryPersonalisationPrompt =
        skipPreorderNotice && (isPreorder || rollsIntoPreorder);

      if (
        declinedPersonalisationOption &&
        formEl.dataset.motMonogramUpsellConfirmed !== 'true' &&
        (forceDiaryPersonalisationPrompt || !monogramUpsellDismissedToday)
      ) {
        window.dispatchEvent(
          new CustomEvent('mot-monogram-upsell', {
            detail: { form: formEl, force: forceDiaryPersonalisationPrompt },
          })
        );
        return;
      }

      if (monogramSelected && !monogramInput?.value.trim()) {
        window.dispatchEvent(
          new CustomEvent('mot-monogram-notice', {
            detail: { form: formEl },
          })
        );
        return;
      }

      if ((isPreorder || rollsIntoPreorder) && !preorderConfirmed && !skipPreorderNotice) {
        window.dispatchEvent(
          new CustomEvent('mot-preorder-notice', {
            detail: {
              form: formEl,
              mode: rollsIntoPreorder ? 'rollover' : 'preorder',
              available,
              requested,
            },
          })
        );
        return;
      }

      await window.motOfferAddon?.(formEl, formEl.closest('[data-product-root]')?.querySelector('[data-mot-magnet-addon]'));

      delete formEl.dataset.motMonogramUpsellConfirmed;
      delete formEl.dataset.motPreorderConfirmed;

      const shortMonogramSelected = selectedMonogramOption?.value.includes('1-3');
      const monogramLimit = shortMonogramSelected ? 3 : 8;
      const monogramIsTooLong = monogramSelected && Array.from(monogramInput?.value || '').length > monogramLimit;

      if (monogramIsTooLong) {
        monogramInput.value = Array.from(monogramInput.value).slice(0, monogramLimit).join('');
        monogramInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      const laserEngravingInput = event.target.querySelector(
        '.laser-engraving-input, input[name="properties[Laser Engraving]"]'
      );
      const selectedLaserOption = Array.from(event.target.querySelectorAll('input:checked')).find((input) =>
        input.name.toLowerCase().includes('laser engraving')
      );
      const shortLaserEngravingSelected = selectedLaserOption?.value.includes('1-3');

      if (shortLaserEngravingSelected && laserEngravingInput?.value.length > 3) {
        laserEngravingInput.value = Array.from(laserEngravingInput.value).slice(0, 3).join('');
      }

      this.$root.dispatchEvent(
        new CustomEvent('baseline:productform:loading', {
          bubbles: true,
        })
      );

      event.preventDefault();

      liveRegion(window.theme.strings.loading);

      const formData = new FormData(event.target);
      const formId = event.target.getAttribute('id');

      const options = fetchConfigDefaults('application/javascript');

      formData.append('sections', 'cart-items,cart-footer,cart-item-count');
      formData.append('sections_url', window.location.pathname);

      options.body = formData;
      options.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete options.headers['Content-Type'];

      const addonMapElement = formEl.closest('[data-product-root]')?.querySelector('[data-mot-display-labels-addon]');
      if (addonMapElement) {
        const addonData = JSON.parse(addonMapElement.textContent);
        const choice = addonMapElement.closest('[data-mot-magnet-addon]')?.querySelector('input[type="radio"]:checked');
        const baseId = addonData.baseVariants[String(formData.get('id'))];
        const magnet = choice?.value ? addonData.magnets[choice.value] : null;
        if (!baseId || !choice || choice.disabled || (choice.value && !magnet?.available)) {
          this.$root.dispatchEvent(new CustomEvent('baseline:productform:error', {
            bubbles: true,
            detail: { sourceId: formId, variantId: Number(formData.get('id')), message: 'This selection is unavailable. Please choose another magnet colour or No.', errors: 'This selection is unavailable. Please choose another magnet colour or No.' },
          }));
          return;
        }
        const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);
        const baseItem = { id: baseId, quantity };
        const properties = {};
        for (const [name, value] of formData.entries()) {
          if (name.startsWith('properties[') && value !== '') properties[name.slice(11, -1)] = value;
        }
        if (Object.keys(properties).length) baseItem.properties = properties;
        if (formData.get('selling_plan')) baseItem.selling_plan = formData.get('selling_plan');
        const items = [baseItem];
        if (magnet) items.push({ id: Number(choice.value), quantity });
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify({ items, sections: 'cart-items,cart-footer,cart-item-count', sections_url: window.location.pathname });
      }

      let response, data;

      try {
        response = await fetch(`${theme.routes.cart_add_url}`, options);
        data = await response.json();
      } catch (error) {
        this.$root.dispatchEvent(new CustomEvent('baseline:productform:error', {
          bubbles: true,
          detail: { sourceId: formId, message: window.theme.strings.cartError },
        }));
        return;
      }

      if (data.status) {
        let errors = data.errors || data.description;
        let message = data.description || data.message;

        const errorDetail = {
          sourceId: formId,
          variantId: Number(formData.get('id')),
          errors,
          message,
        };

        this.$root.dispatchEvent(
          new CustomEvent('baseline:productform:error', {
            detail: errorDetail,
            bubbles: true,
          })
        );

        const formEl = this.formEl;
        const productRootEl = formEl.closest('[data-product-root]') || null;

        document.dispatchEvent(
          new CustomEvent('theme:product:error:add-to-cart', {
            detail: {
              productRootEl,
              formEl,
              ...errorDetail,
            },
          })
        );
      } else {
        if (addonMapElement) {
          try {
            if (typeof window.upcartRefreshCart === 'function') await window.upcartRefreshCart();
            if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
          } catch (error) {
            console.warn('MOT magnets: UpCart refresh failed', error);
          }
        }
        this.$root.dispatchEvent(
          new CustomEvent('baseline:productform:success', {
            bubbles: true,
          })
        );

        document.body.dispatchEvent(
          new CustomEvent('baseline:cart:afteradditem', {
            bubbles: true,
            detail: {
              response: data,
              sourceId: formId,
            },
          })
        );
      }
    },
  }));
});

